import { View, StyleSheet } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Controller, FormProvider } from 'react-hook-form';
import { z } from 'zod';

import { Email } from '@pletly/feature-auth';
import { Button, FormInput, FormPeriodControl, spacing, useForm } from '@pletly/ui';
import { CareRecipientsSelect, CareRecipientsSelectProps } from '../CareRecipientsSelect';

const DURATION_OPTIONS_DAYS = ['1', '7', '30'] as const satisfies string[];

const formSchema = z.discriminatedUnion('accessDuration', [
	z.object({
		email: z.string().email(),
		careRecipients: z.array(z.string()).or(z.undefined()),
		accessDuration: z.literal('custom'),
		customAccessDuration: z.object({
			startDate: z.date(),
			endDate: z.date(),
		}),
	}),
	z.object({
		email: z.string().email(),
		careRecipients: z.array(z.string()).or(z.undefined()),
		accessDuration: z.enum(DURATION_OPTIONS_DAYS),
	}),
]);

export type FormData = z.infer<typeof formSchema>;

interface Props {
	onSubmit: (data: FormData, resetValues: () => void) => unknown;
	saving: boolean;
	withCareRecipientsSelect?: boolean;
	careRecipientsToSelect: CareRecipientsSelectProps['careRecipientsToSelect'];
}

export const TemporaryAccessForm = ({
	onSubmit,
	saving,
	withCareRecipientsSelect = false,
	careRecipientsToSelect,
}: Props) => {
	const { t } = useTranslation();

	const formMethods = useForm(formSchema);

	return (
		<View style={styles.form}>
			<FormProvider {...formMethods}>
				<FormInput
					defaultValue=""
					InputComponent={Email}
					textContentType="emailAddress"
					name="email"
					gutter={false}
				/>
				{withCareRecipientsSelect && (
					<Controller
						name="careRecipients"
						control={formMethods.control}
						defaultValue={[]}
						render={({ field: { onChange, value }, fieldState: { error } }) => (
							<CareRecipientsSelect
								careRecipientsToSelect={careRecipientsToSelect}
								value={value ?? []}
								onSelect={onChange}
								label={t('temporary-access.label-select-care-recipients')}
								placeholder={t('temporary-access.placeholder-select-care-recipients')}
								error={!!error}
								errors={error?.message ? [error?.message] : undefined}
							/>
						)}
					/>
				)}
				<FormPeriodControl
					dayOptions={DURATION_OPTIONS_DAYS}
					radioGroupProps={{
						name: 'accessDuration',
						label: t('temporary-access.label-access-duration'),
					}}
					rangeDatepickerProps={{
						name: 'customAccessDuration',
						label: t('temporary-access.label-custom-duration'),
						placeholder: t('temporary-access.label-custom-duration'),
						min: new Date(),
					}}
				/>
			</FormProvider>
			<Button
				variant="filled"
				color="primary"
				loading={saving}
				onPress={formMethods.handleSubmit((data) => onSubmit(data, formMethods.reset))}
			>
				{t('temporary-access.send-invite')}
			</Button>
		</View>
	);
};

const styles = StyleSheet.create({
	form: {
		gap: spacing(4),
	},
});
